import { useLocalization } from '@components/Localization'
import { Box, CleanButton, Flex, Heading, Icon, Image, Link, Text } from '@gassan-ui'
import { CartItem } from '@generated'
import { imgix } from '@lib/imigx'
import { motion } from 'framer-motion'
import React, { FC } from 'react'
import { Description, Parent } from './elements'
import Price from './Price'
import SelectQuantity from './SelectQuantity'
import SelectSize from './SelectSize'
import SizeInfo from './SizeInfo'

type Props = {
  variant: 'loading' | 'updating' | 'idle'
  onRemove: () => void
  onChangeQuantity: (quantity: number) => void
  onChangeSize: (size: string) => void
  item: CartItem
}

const CartRow: FC<Props> = ({ variant, onRemove, onChangeQuantity, onChangeSize, item }) => {
  const { lang } = useLocalization()
  if (!item.article) {
    return null
  }

  const isRing = item.article.articleAttributes
    ? item.article.articleAttributes.findIndex(
        attr => attr.attributeID === 15 && attr.attributeValue === 'Ring'
      ) > -1
    : false

  return (
    <motion.div animate exit={{ opacity: 0 }} transition={{ duration: 0.2, ease: 'easeOut' }}>
      <Parent alignItems="flex-start">
        <Box bg="shades.100" position="relative" paddingTop="100%">
          {item.article.images[0] && (
            <Image
              height="100%"
              width="100%"
              position="absolute"
              top="0"
              sources={[imgix(item.article.images[0], { width: 200 })]}
              alt={item.article.brand}
              backgroundColor="shades.100"
            />
          )}
        </Box>
        <Box display={{ _: 'flex' }} flexDirection="column">
          <Flex justifyContent="space-between" alignItems="center">
            <Heading mb={{ _: 0, small: '0.5rem', xlarge: '1rem' }} variant="h4">
              {item.article.brand}
            </Heading>
            <Box display={['contents', 'none']} color="tabaccoBrown">
              <CleanButton onClick={onRemove}>
                <Icon icon="trash-can" />
              </CleanButton>
            </Box>
          </Flex>
          <Box display={['contents', 'flex']} alignItems="center">
            <Description mb={{ _: '0.25rem', small: '0.5rem', large: '1rem' }}>
              {item.article.description}
            </Description>
            <Box display={['contents', 'none']}>
              <Price
                price={item.article.price * item.quantity}
                salePrice={item.article.salePrice && item.article.salePrice * item.quantity}
                discount={
                  item.article.salePrice &&
                  Math.round((1 - item.article.salePrice / item.article.price) * 100)
                }
                status={variant === 'updating' ? 'loading' : 'idle'}
              />
            </Box>
          </Box>
          <Box>
            <Box
              display={{ _: 'grid', small: 'inline-grid', xlarge: 'none' }}
              gridTemplateColumns={{ _: '4.75rem 1fr', small: '4.75rem 9.5rem' }}
              mb={['0.5rem', '1rem']}
            >
              <SelectQuantity
                quantity={item.quantity}
                onChangeQuantity={quantity => onChangeQuantity(quantity)}
              />
              {isRing && <SelectSize size={item.size} onChangeSize={size => onChangeSize(size)} />}
            </Box>
            <Box
              display={{
                _: 'contents',
                small: item.size !== '' ? 'inline-block' : 'block',
                xlarge: 'none'
              }}
              ml={{ _: 0, small: item.size !== '' && '1rem', xlarge: 0 }}
              mb={[0, '1.5rem']}
            >
              {isRing && <SizeInfo size={item.size} />}
            </Box>
            <Box display={['none', 'block']}>
              <Link as="button" onClick={onRemove} variant="brown">
                {lang === 'nl' ? 'Verwijder' : 'Remove'}
              </Link>
            </Box>
          </Box>
        </Box>
        <Box display={{ _: ' none', xlarge: 'block' }}>
          {isRing && (
            <>
              <Text mb="0.5rem" variant="small" as="span" display="block">
                {lang === 'nl' ? 'Maat' : 'Size'}
              </Text>
              <SelectSize size={item.size} onChangeSize={size => onChangeSize(size)} />
              <Box mt="0.5rem">
                <SizeInfo size={item.size} />
              </Box>
            </>
          )}
        </Box>
        <Box display={{ _: ' none', xlarge: 'block' }}>
          <Text mb="0.5rem" variant="small" as="span" display="block">
            {lang === 'nl' ? 'Aantal' : 'Quantity'}
          </Text>
          <SelectQuantity
            quantity={item.quantity}
            onChangeQuantity={quantity => onChangeQuantity(quantity)}
          />
        </Box>
        <Box display={['none', 'contents']}>
          <Price
            price={item.article.price * item.quantity}
            salePrice={item.article.salePrice && item.article.salePrice * item.quantity}
            discount={
              item.article.salePrice &&
              Math.round((1 - item.article.salePrice / item.article.price) * 100)
            }
            status={variant === 'updating' ? 'loading' : 'idle'}
          />
        </Box>
      </Parent>
    </motion.div>
  )
}

export default CartRow
